import { navigate } from 'gatsby';
import React, { useState, useRef } from 'react';
import PhoneCodes from './partials/phone_codes';
import PostHogHelper from '../../utils/posthog-helper';
import BunnyLogo from '../fragments/bunny-logo';

export const ContactType = {
  Contact: 0,
  Sales: 1,
  Abuse: 2,
};

const FormError = ({ submitted, success }) => (
  <>
    {submitted && !success && (
      <div className='error-message'>
        There was an error submitting this contact form. Please try again or
        send us an email at sales@bunny.net.
      </div>
    )}
  </>
);

const CampaignForm: React.FC<{ block: any }> = ({ block }) => {
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const { campaign_type } = block;

  const handlePhoneCode = phoneCode => {
    setSelectedCountry(phoneCode);
  };

  const [formState, setFormState] = useState({
    FullName: '',
    FirstName: '',
    LastName: '',
    ContactEmail: '',
    CompanyName: '',
    PhoneNumber: '',
    MeetupDate: '',
    MeetupPerson: '',
    Message: '',
    AgreeWithTerms: false,
    Campaign: '',
    Type: ContactType.Sales,
    IsValid: 'false',
  });

  const changeFormState = (event: any) => {
    const value = event.target.matches('[type="checkbox"]')
      ? event.target.checked
      : event.target.value;

    setFormState({
      ...formState,
      [event.target.name]: value,
    });
  };

  const determineCampaign = () => {
    if (campaign_type === 'ibc') {
      return 'IBC 2023';
    } else if (campaign_type === 'nab') {
      return 'NAB 2024';
    } else if (campaign_type === 'broadcast-asia') {
      return 'Broadcast Asia 2024';
    } else if (campaign_type === 'general-2024') {
      return 'Events 2024';
    } else if (campaign_type === 'nem-2024') {
      return 'NEM 2024';
    }
  };

  const determineMessage = (campaign: string) => {
    let message = `Campaign: ${campaign}`;

    if (formState.MeetupPerson !== '') {
      message = `${message}\nMeetup Person: ${formState.MeetupPerson}`;
    }
    if (formState.MeetupDate !== '') {
      message = `${message}\nMeetup Date: ${formState.MeetupDate}`;
    }
    if (formState.Message !== '') {
      message = `${message}\nMessage: ${formState.Message}`;
    }

    return message;
  };

  const determineTitle = () => {
    if (campaign_type === 'ibc') {
      return 'Book a meeting at IBC 2023';
    } else if (campaign_type === 'nab') {
      return 'Book a meeting at NAB 2024';
    } else if (campaign_type === 'broadcast-asia') {
      return 'Book a meeting at Broadcast Asia 2024';
    } else if (campaign_type === 'broadcast-asia') {
      return 'Book a meeting at Bunny.net 2024 events';
    } else if (campaign_type === 'nem-2024') {
      return 'Book a meeting at NEM 2024';
    }
  };

  const submitHandler = (event: any) => {
    event.preventDefault();

    const formCampaign = determineCampaign();
    const formTitle = determineTitle();
    const formMessage = determineMessage(formCampaign);

    const body = {
      CompanyName: formState.CompanyName,
      ContactEmail: formState.ContactEmail,
      Phone: `+${selectedCountry} ${formState.PhoneNumber}`,
      Message: formMessage,
      IsValid: formState.IsValid,
      Type: `${formState.Type}`,
      AgreeWithTerms: formState.AgreeWithTerms
        ? 'I have read and agree to the bunny.net Privacy Policy.'
        : '',
      Campaign: formCampaign,
      FullName: !formState.FullName
        ? `${formState.FirstName} ${formState.LastName}`
        : formState.FullName,
      Services: '',
      Title: formTitle,
    };

    fetch(process.env.GATSBY_API_ENDPOINT + '/contact/submit-form', {
      method: 'post',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }).then(async response => {
      setSubmitted(true);

      if (response.ok) {
        setSuccess(true);
        await PostHogHelper.promiseToCaptureEmail(body.ContactEmail);
        navigate(block.thank_you_page);
      } else {
        setSuccess(false);
      }
    });
  };

  return (
    <div className='contact-form contact-form--campaign'>
      <div className='campaign-form__container'>
        <div className='campaign-form__content'>
          <div className='campaign-form__title'>
            <h2 dangerouslySetInnerHTML={{ __html: block.title }} />
          </div>
          <div className='campaign-form__description'>
            <p>{block.description}</p>
          </div>
          <div className='form-wr'>
            {campaign_type === 'ibc' && (
              <IBCForm
                block={block}
                formState={formState}
                changeFormState={changeFormState}
                submitHandler={submitHandler}
                submitted={submitted}
                success={success}
                handlePhoneCode={handlePhoneCode}
                dates={block.dates}
              />
            )}
            {campaign_type === 'nab' && (
              <NabAndAsiaForm
                block={block}
                formState={formState}
                changeFormState={changeFormState}
                submitHandler={submitHandler}
                submitted={submitted}
                success={success}
                handlePhoneCode={handlePhoneCode}
                meetupDateLabelSuffix='NAB meetup'
                dates={block.dates}
                people={block.people}
              />
            )}
            {campaign_type === 'broadcast-asia' && (
              <NabAndAsiaForm
                block={block}
                formState={formState}
                changeFormState={changeFormState}
                submitHandler={submitHandler}
                submitted={submitted}
                success={success}
                handlePhoneCode={handlePhoneCode}
                meetupDateLabelSuffix='Broadcast Asia meetup'
                dates={block.dates}
                people={block.people}
              />
            )}
            {campaign_type === 'general-2024' && (
              <General2024Form
                block={block}
                formState={formState}
                changeFormState={changeFormState}
                submitHandler={submitHandler}
                submitted={submitted}
                success={success}
                handlePhoneCode={handlePhoneCode}
                dates={block.dates}
                people={block.people}
              />
            )}
            {campaign_type === 'nem-2024' && (
              <NEM24Form
                block={block}
                formState={formState}
                changeFormState={changeFormState}
                submitHandler={submitHandler}
                submitted={submitted}
                success={success}
                handlePhoneCode={handlePhoneCode}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const NabAndAsiaForm: React.FC<{
  block: any;
  formState: any;
  changeFormState: any;
  submitHandler: any;
  submitted: boolean;
  success: boolean;
  handlePhoneCode: any;
  meetupDateLabelSuffix: string;
  dates: string[];
  people: string[];
}> = ({
  formState,
  changeFormState,
  submitHandler,
  submitted,
  success,
  handlePhoneCode,
  meetupDateLabelSuffix,
  dates,
  people,
}) => {
  return (
    <form onSubmit={submitHandler}>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='FullName'>Full Name</label>
            <input
              type='text'
              name='FullName'
              id='FullName'
              value={formState.FullName}
              onChange={changeFormState}
              placeholder='Your Full Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='ContactEmail'>Email</label>
            <input
              type='email'
              name='ContactEmail'
              id='ContactEmail'
              value={formState.ContactEmail}
              onChange={changeFormState}
              placeholder='Your Email'
              required
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='CompanyName'>Company Name</label>
            <input
              type='text'
              name='CompanyName'
              id='CompanyName'
              value={formState.CompanyName}
              onChange={changeFormState}
              placeholder='Your Company Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item form-select'>
            <label htmlFor='MeetupDate'>
              Preferred date for {meetupDateLabelSuffix}
            </label>
            <select
              id='MeetupDate'
              name='MeetupDate'
              value={formState.MeetupDate}
              onChange={changeFormState}
              required
            >
              {dates.map(date => (
                <option
                  key={date}
                  value={date}
                >
                  {date}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='PhoneNumber'>Phone Number</label>
            <div className='phone-wr'>
              <PhoneCodes setPhoneCode={handlePhoneCode} />
              <input
                type='text'
                name='PhoneNumber'
                id='PhoneNumber'
                value={formState.PhoneNumber}
                onChange={changeFormState}
                placeholder='xxx-xxx-xxxx'
                required
              />
            </div>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item form-select'>
            <label htmlFor='MeetupPerson'>
              Who would you like to meet with?
            </label>
            <select
              id='MeetupPerson'
              name='MeetupPerson'
              value={formState.MeetupPerson}
              onChange={changeFormState}
              required
            >
              {people.map(person => (
                <option
                  key={person}
                  value={person}
                >
                  {person}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12'>
          <div className='form-item'>
            <label htmlFor='Message'>Message</label>
            <textarea
              name='Message'
              id='Message'
              value={formState.Message}
              onChange={changeFormState}
              placeholder={`Tell us about your project or what you'd like to discuss at ${meetupDateLabelSuffix}`}
              required
              minLength={10}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12 form-submit'>
          <input
            type='hidden'
            id='IsValid'
            name='IsValid'
            value={formState.IsValid}
            onChange={changeFormState}
          />
          <input
            type='hidden'
            id='Type'
            name='Type'
            value={formState.Type}
            onChange={changeFormState}
          />
          <div className='form-terms'>
            <label htmlFor='AgreeWithTerms'>
              <input
                type='checkbox'
                name='AgreeWithTerms'
                id='AgreeWithTerms'
                checked={formState.AgreeWithTerms}
                onChange={changeFormState}
                required
              />
              I have read and agree to the bunny.net{' '}
              <a
                className='policy-link'
                href='/privacy'
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <button
            type='submit'
            name='submit'
            className='form-action'
            value='Book a meeting'
          >
            Book a Meeting
          </button>
          <FormError
            submitted={submitted}
            success={success}
          />
        </div>
      </div>
    </form>
  );
};

const General2024Form: React.FC<{
  block: any;
  formState: any;
  changeFormState: any;
  submitHandler: any;
  submitted: boolean;
  success: boolean;
  handlePhoneCode: any;
  dates: string[];
  people: string[];
}> = ({
  formState,
  changeFormState,
  submitHandler,
  submitted,
  success,
  handlePhoneCode,
  dates,
  people,
}) => {
  return (
    <form onSubmit={submitHandler}>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='FullName'>Full Name</label>
            <input
              type='text'
              name='FullName'
              id='FullName'
              value={formState.FullName}
              onChange={changeFormState}
              placeholder='Your Full Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='ContactEmail'>Email</label>
            <input
              type='email'
              name='ContactEmail'
              id='ContactEmail'
              value={formState.ContactEmail}
              onChange={changeFormState}
              placeholder='Your Email'
              required
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='CompanyName'>Company Name</label>
            <input
              type='text'
              name='CompanyName'
              id='CompanyName'
              value={formState.CompanyName}
              onChange={changeFormState}
              placeholder='Your Company Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item form-select'>
            <label htmlFor='MeetupDate'>Event Location</label>
            <select
              id='MeetupDate'
              name='MeetupDate'
              value={formState.MeetupDate}
              onChange={changeFormState}
              required
            >
              {dates.map(date => (
                <option
                  key={date}
                  value={date}
                >
                  {date}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='PhoneNumber'>Phone Number</label>
            <div className='phone-wr'>
              <PhoneCodes setPhoneCode={handlePhoneCode} />
              <input
                type='text'
                name='PhoneNumber'
                id='PhoneNumber'
                value={formState.PhoneNumber}
                onChange={changeFormState}
                placeholder='xxx-xxx-xxxx'
                required
              />
            </div>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item form-select'>
            <label htmlFor='MeetupPerson'>
              Who would you like to meet with?
            </label>
            <select
              id='MeetupPerson'
              name='MeetupPerson'
              value={formState.MeetupPerson}
              onChange={changeFormState}
              required
            >
              {people.map(person => (
                <option
                  key={person}
                  value={person}
                >
                  {person}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12'>
          <div className='form-item'>
            <label htmlFor='Message'>Message</label>
            <textarea
              name='Message'
              id='Message'
              value={formState.Message}
              onChange={changeFormState}
              placeholder="Tell us about your project or what you'd like to discuss."
              required
              minLength={10}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12 form-submit'>
          <input
            type='hidden'
            id='IsValid'
            name='IsValid'
            value={formState.IsValid}
            onChange={changeFormState}
          />
          <input
            type='hidden'
            id='Type'
            name='Type'
            value={formState.Type}
            onChange={changeFormState}
          />
          <div className='form-terms'>
            <label htmlFor='AgreeWithTerms'>
              <input
                type='checkbox'
                name='AgreeWithTerms'
                id='AgreeWithTerms'
                checked={formState.AgreeWithTerms}
                onChange={changeFormState}
                required
              />
              I have read and agree to the bunny.net{' '}
              <a
                className='policy-link'
                href='/privacy'
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <button
            type='submit'
            name='submit'
            className='form-action'
            value='Book a meeting'
          >
            Book a Meeting
          </button>
          <FormError
            submitted={submitted}
            success={success}
          />
        </div>
      </div>
    </form>
  );
};

const IBCForm: React.FC<{
  block: any;
  formState: any;
  changeFormState: any;
  submitHandler: any;
  submitted: boolean;
  success: boolean;
  handlePhoneCode: any;
  dates: string[];
}> = ({
  formState,
  changeFormState,
  submitHandler,
  submitted,
  success,
  handlePhoneCode,
  dates,
}) => {
  return (
    <form onSubmit={submitHandler}>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='FirstName'>First Name</label>
            <input
              type='text'
              name='FirstName'
              id='FirstName'
              value={formState.FirstName}
              onChange={changeFormState}
              placeholder='Your Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='LastName'>Last Name</label>
            <input
              type='text'
              name='LastName'
              id='LastName'
              value={formState.LastName}
              onChange={changeFormState}
              placeholder='Your Last Name'
              required
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='ContactEmail'>Email</label>
            <input
              type='email'
              name='ContactEmail'
              id='ContactEmail'
              value={formState.ContactEmail}
              onChange={changeFormState}
              placeholder='Your Email'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='CompanyName'>Company Name</label>
            <input
              type='text'
              name='CompanyName'
              id='CompanyName'
              value={formState.CompanyName}
              onChange={changeFormState}
              placeholder='Your Company Name'
              required
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='PhoneNumber'>Phone Number</label>
            <div className='phone-wr'>
              <PhoneCodes setPhoneCode={handlePhoneCode} />
              <input
                type='text'
                name='PhoneNumber'
                id='PhoneNumber'
                value={formState.PhoneNumber}
                onChange={changeFormState}
                placeholder='xxx-xxx-xxxx'
                required
              />
            </div>
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item form-select'>
            <label htmlFor='MeetupDate'>Meetup Date</label>
            <select
              id='MeetupDate'
              name='MeetupDate'
              value={formState.MeetupDate}
              onChange={changeFormState}
              required
            >
              {dates.map(date => (
                <option
                  key={date}
                  value={date}
                >
                  {date}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12'>
          <div className='form-item'>
            <label htmlFor='Message'>Message</label>
            <textarea
              name='Message'
              id='Message'
              value={formState.Message}
              onChange={changeFormState}
              placeholder='Tell us about your project and how bunny.net can help you.'
              required
              minLength={10}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12 form-submit'>
          <input
            type='hidden'
            id='IsValid'
            name='IsValid'
            value={formState.IsValid}
            onChange={changeFormState}
          />
          <input
            type='hidden'
            id='Type'
            name='Type'
            value={formState.Type}
            onChange={changeFormState}
          />
          <div className='form-terms'>
            <label htmlFor='AgreeWithTerms'>
              <input
                type='checkbox'
                name='AgreeWithTerms'
                id='AgreeWithTerms'
                checked={formState.AgreeWithTerms}
                onChange={changeFormState}
                required
              />
              I have read and agree to the bunny.net{' '}
              <a
                className='policy-link'
                href='/privacy'
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <button
            type='submit'
            name='submit'
            className='form-action'
            value='Book a meeting'
          >
            Book a Meeting
          </button>
          <FormError
            submitted={submitted}
            success={success}
          />
        </div>
      </div>
    </form>
  );
};

const NEM24Form: React.FC<{
  block: any;
  formState: any;
  changeFormState: any;
  submitHandler: any;
  submitted: boolean;
  success: boolean;
  handlePhoneCode: any;
}> = ({
  formState,
  changeFormState,
  submitHandler,
  submitted,
  success,
  handlePhoneCode,
}) => {
  return (
    <form onSubmit={submitHandler}>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='FullName'>Full Name</label>
            <input
              type='text'
              name='FullName'
              id='FullName'
              value={formState.FullName}
              onChange={changeFormState}
              placeholder='Your Full Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='ContactEmail'>Email</label>
            <input
              type='email'
              name='ContactEmail'
              id='ContactEmail'
              value={formState.ContactEmail}
              onChange={changeFormState}
              placeholder='Your Email'
              required
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='CompanyName'>Company Name</label>
            <input
              type='text'
              name='CompanyName'
              id='CompanyName'
              value={formState.CompanyName}
              onChange={changeFormState}
              placeholder='Your Company Name'
              required
            />
          </div>
        </div>
        <div className='col-md-6'>
          <div className='form-item'>
            <label htmlFor='PhoneNumber'>Phone Number</label>
            <div className='phone-wr'>
              <PhoneCodes setPhoneCode={handlePhoneCode} />
              <input
                type='text'
                name='PhoneNumber'
                id='PhoneNumber'
                value={formState.PhoneNumber}
                onChange={changeFormState}
                placeholder='xxx-xxx-xxxx'
                required
              />
            </div>
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12'>
          <div className='form-item'>
            <label htmlFor='Message'>Message</label>
            <textarea
              name='Message'
              id='Message'
              value={formState.Message}
              onChange={changeFormState}
              placeholder={`Tell us about your project and how bunny.net can help you`}
              required
              minLength={10}
            />
          </div>
        </div>
      </div>
      <div className='row'>
        <div className='col-12 form-submit'>
          <input
            type='hidden'
            id='IsValid'
            name='IsValid'
            value={formState.IsValid}
            onChange={changeFormState}
          />
          <input
            type='hidden'
            id='Type'
            name='Type'
            value={formState.Type}
            onChange={changeFormState}
          />
          <div className='form-terms'>
            <label htmlFor='AgreeWithTerms'>
              <input
                type='checkbox'
                name='AgreeWithTerms'
                id='AgreeWithTerms'
                checked={formState.AgreeWithTerms}
                onChange={changeFormState}
                required
              />
              I have read and agree to the bunny.net{' '}
              <a
                className='policy-link'
                href='/privacy'
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <button
            type='submit'
            name='submit'
            className='form-action'
            value='Claim your Credits'
          >
            Claim your Credits
          </button>
          <FormError
            submitted={submitted}
            success={success}
          />
        </div>
      </div>
    </form>
  );
};

const IBCLogo = ({ src }) => {
  if (!src) {
    return null;
  }

  return (
    <div className='logo logo--ibc'>
      <img
        src={src}
        alt='IBC'
        title='IBC'
      />
    </div>
  );
};

const Campaign: React.FC<{ block: any }> = ({ block }) => {
  return (
    <section className={`block ${block._template} ${block.css_class_name}`}>
      <div className='left'>
        <BunnyLogo variant='light' />
      </div>
      <div className='right'>
        <BunnyLogo variant='dark' />
        <img
          className='campaign-image'
          src={block.left.image}
          alt={block.left.image_alt_text}
        />
        <div className='campaign-info'>
          <div className='campaign-title'>
            <h2 dangerouslySetInnerHTML={{ __html: block.right.title }} />
          </div>
          <div className='campaign-description'>
            <p>{block.right.text}</p>
          </div>
          <CampaignForm block={block} />
        </div>
        <IBCLogo src={block.right.logo} />
      </div>
    </section>
  );
};

export default Campaign;
